import { useMemo, useRef, useState } from "react";
import { ColDef, ColumnState } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { RequiredItem } from "./PDA1121_R01Dto";
import usePDA1121_R01Store from "./usePDA1121_R01Store";
import { aggridHeaderClass, pdaCellStyle } from "src/components/common/common";

type RequiredItemRow = RequiredItem & { inserted?: boolean };

const PDA1121_R01DetailTable = () => {
  const { selectedOrder, requiredItems, scanResult } = usePDA1121_R01Store();
  const gridRef = useRef<AgGridReact<RequiredItemRow>>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [, setHoveredRowIndex] = useState<number | null>(null);
  const [, setColumnState] = useState<ColumnState[] | null>(null);

  const paddedGridData = useMemo(
    () => requiredItems.map((item) => ({ ...item, inserted: false })),
    [requiredItems]
  );

  // 마지막으로 일치한 itemCd (행 강조용)
  const matchedItemCd = scanResult?.matched ? scanResult.itemCd : null;

  const columnDefs: ColDef<RequiredItemRow>[] = useMemo(
    () => [
      {
        headerName: "피더",
        field: "feederCd",
        valueGetter: (p) => p.data?.feederCd || "-",
        cellStyle: (p) => ({
          textAlign: "center",
          fontWeight: p.data?.itemCd === matchedItemCd ? "bold" : "normal",
          backgroundColor: p.data?.itemCd === matchedItemCd ? "#dcfce7" : "white",
          ...pdaCellStyle,
        }),
        headerClass: aggridHeaderClass,
        flex: 0.9,
        minWidth: 80,
      },
      {
        headerName: "품명",
        field: "itemNm",
        valueGetter: (p) => p.data?.itemNm ?? "-",
        cellStyle: (p) => ({
          textAlign: "left",
          backgroundColor: p.data?.itemCd === matchedItemCd ? "#dcfce7" : "white",
          ...pdaCellStyle,
        }),
        headerClass: aggridHeaderClass,
        flex: 1.5,
        minWidth: 80,
      },
      {
        headerName: "규격",
        field: "itemSpec",
        valueGetter: (p) => p.data?.itemSpec ?? "-",
        cellStyle: (p) => ({
          textAlign: "left",
          backgroundColor: p.data?.itemCd === matchedItemCd ? "#dcfce7" : "white",
          ...pdaCellStyle,
        }),
        headerClass: aggridHeaderClass,
        flex: 1.2,
        minWidth: 80,
      },
      {
        headerName: "필요량",
        field: "requiredQty",
        valueGetter: (p) => (p.data?.requiredQty != null ? p.data.requiredQty.toLocaleString() : "-"),
        cellStyle: (p) => ({
          textAlign: "right",
          justifyContent: "flex-end",
          backgroundColor: p.data?.itemCd === matchedItemCd ? "#dcfce7" : "white",
          ...pdaCellStyle,
        }),
        headerClass: aggridHeaderClass,
        flex: 0.8,
        minWidth: 80,
      },
    ],
    [matchedItemCd]
  );

  return (
    <div className="flex-1 p-3 pt-0 flex w-full h-full flex-col overflow-hidden">
      {!selectedOrder ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <p className="text-lg mb-2">📋</p>
            <p>공정지시를 선택하세요</p>
          </div>
        </div>
      ) : requiredItems.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <p className="text-lg mb-2">📋</p>
            <p>등록된 필요자재가 없습니다</p>
          </div>
        </div>
      ) : (
        <GridContextWrapper
          gridRef={gridRef}
          gridWrapperRef={gridWrapperRef}
          paddedGridData={paddedGridData}
          initSelected={() => {}}
          baseColumnDefs={columnDefs}
          setColumnState={setColumnState}
          localStorageKey="PDA1121_R01_MaterialTable_ColumnState"
          enableDataHistory={false}
        >
          <div className="flex-1 w-full h-full min-h-0">
            <Grid_Select<RequiredItemRow>
              ref={gridRef}
              rowData={paddedGridData}
              columnDefs={columnDefs}
              setHoveredRowIndex={setHoveredRowIndex}
              columnStateKey="PDA1121_R01_MaterialTable_ColumnState"
              setColumnState={setColumnState}
            />
          </div>
        </GridContextWrapper>
      )}
      {selectedOrder && requiredItems.length > 0 && (
        <div className="px-2 py-2 border-t border-gray-200 bg-white">
          <p className="text-sm text-gray-500 text-center">
            릴을 스캔하면 일치 피더 행이 강조됩니다 · 저장되지 않습니다
          </p>
        </div>
      )}
    </div>
  );
};

export default PDA1121_R01DetailTable;
